import { Injectable } from '@angular/core';
import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';

import {
  USER_ADMIN_GMA,
  UserAdminGmaWebcamValue,
} from '../constants/user-admin-contract.constants';

@Injectable({
  providedIn: 'root',
})
export class UserAdminFormFactoryService {
  private readonly gmaProductKeyPattern =
    /^[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}$/;

  createGmaRow(
    coordinates = '',
    manufacturer = '',
    webcam: UserAdminGmaWebcamValue = USER_ADMIN_GMA.defaultWebcamValue,
    productKey = ''
  ): UntypedFormGroup {
    const row = new UntypedFormGroup(
      {
        id: new UntypedFormControl(crypto.randomUUID()),
        gmaCoordinates: new UntypedFormControl(coordinates),
        gmaManufacturer: new UntypedFormControl(manufacturer),
        gmaWebcam: new UntypedFormControl(webcam),
        gmaProductKey: new UntypedFormControl(productKey),
      },
      {
        validators: this.campbellProductKeyValidator(),
      }
    );

    row.updateValueAndValidity({ emitEvent: false });

    return row;
  }

  createContactRow(name = '', email = '', phone = ''): UntypedFormGroup {
    return new UntypedFormGroup({
      id: new UntypedFormControl(crypto.randomUUID()),
      name: new UntypedFormControl(name),
      email: new UntypedFormControl(email),
      phone: new UntypedFormControl(phone),
    });
  }

  private campbellProductKeyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const manufacturer = control.get('gmaManufacturer')?.value;
      const productKeyControl = control.get('gmaProductKey');

      if (!productKeyControl) {
        return null;
      }

      const productKey = productKeyControl.value;
      const isCampbellSolar =
        manufacturer === USER_ADMIN_GMA.campbellWinterSenseSolar;

      this.removeControlErrors(productKeyControl, [
        'required',
        'pattern',
        'gmaProductKeyRequired',
        'gmaProductKeyPattern',
      ]);

      if (!isCampbellSolar) {
        return null;
      }

      if (!productKey) {
        this.addControlError(productKeyControl, 'required', true);
        this.addControlError(productKeyControl, 'gmaProductKeyRequired', true);

        return {
          gmaProductKeyRequired: true,
        };
      }

      if (!this.gmaProductKeyPattern.test(productKey)) {
        this.addControlError(productKeyControl, 'pattern', {
          requiredPattern: this.gmaProductKeyPattern.toString(),
          actualValue: productKey,
        });
        this.addControlError(productKeyControl, 'gmaProductKeyPattern', true);

        return {
          gmaProductKeyPattern: true,
        };
      }

      return null;
    };
  }

  private addControlError(
    control: AbstractControl,
    errorKey: string,
    errorValue: unknown
  ): void {
    control.setErrors({
      ...(control.errors ?? {}),
      [errorKey]: errorValue,
    });
  }

  private removeControlErrors(
    control: AbstractControl,
    errorKeys: string[]
  ): void {
    const currentErrors = control.errors;

    if (!currentErrors) {
      return;
    }

    const remainingErrors = { ...currentErrors };

    for (const errorKey of errorKeys) {
      delete remainingErrors[errorKey];
    }

    control.setErrors(
      Object.keys(remainingErrors).length > 0 ? remainingErrors : null
    );
  }
}
